import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { AutoComplete } from 'primereact/autocomplete'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HorseRegisterDto, Horse, HorseRegisterApi, HorseApi, HorseSearchResultDto, PersonApi, PersonSearchResultDto } from '../../../api'
import { useLocalState } from '../../../shared/UseLocalState'
import { appState } from '../../../state/App.state'
import { authState } from '../../../state/Auth.state'
import { horsesState } from '../../../state/Horses.state'
import { Button } from 'primereact/button'
import { Loading } from '../../loading/Loading'

interface LookupItem {
  id: number
  name: string | null
  shortcut?: string | null
}

const NULL_DATE = new Date('1800-01-01')

const parseDate = (dd: string, mm: string, yyyy: string): Date => {
  const day = Number(dd)
  const month = Number(mm)
  const year = Number(yyyy)
  if (!day || !month || !year || yyyy.length < 4) return NULL_DATE
  const date = new Date(year, month - 1, day)
  date.setFullYear(year)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return NULL_DATE
  return date
}
const EMPTY_HORSE_REGISTER: HorseRegisterDto = {
  name: '',
  breedId: undefined,
  colorId: undefined,
  gender: '',
  father: '',
  fatherBreedId: undefined,
  fatherRegisterId: undefined,
  mother: '',
  motherBreedId: undefined,
  motherRegisterId: undefined,
  birthday: NULL_DATE,
  owner: '',
  remark: '',
  size: undefined,
  isBigHorse: false,
  lNr: ''
}

//if (!bookerId) throw new Error(`booker id is mandatory`)

export const Horses = () => {
  /* STATE */
  const bookerId = authState.user$.value?.registerId
  const bookerEmail = authState.user$.value?.email
  const horses = useLocalState<Horse[]>(horsesState.horses$, horsesState.horses$.value)

  const [loading, setLoading] = useState<boolean>(false)
  const [form, setForm] = useState<HorseRegisterDto>(EMPTY_HORSE_REGISTER)
  const [ownedByBooker, setOwnedByBooker] = useState(false)
  const [breeds, setBreeds] = useState<LookupItem[]>([])
  const [colors, setColors] = useState<LookupItem[]>([])
  const [motherSearch, setMotherSearch] = useState<string>('')
  const [motherSuggestions, setMotherSuggestions] = useState<HorseSearchResultDto[]>([])
  const [fatherSearch, setFatherSearch] = useState<string>('')
  const [fatherSuggestions, setFatherSuggestions] = useState<HorseSearchResultDto[]>([])
  const [breederSearch, setBreederSearch] = useState<string>('')
  const [breederSuggestions, setBreederSuggestions] = useState<PersonSearchResultDto[]>([])
  const [bdDay, setBdDay] = useState('')
  const [bdMonth, setBdMonth] = useState('')
  const [bdYear, setBdYear] = useState('')
  const refBdMonth = useRef<HTMLInputElement>(null)
  const refBdYear = useRef<HTMLInputElement>(null)

  /* EFFECTS */
  useEffect(() => {
    const basePath = authState.apicfg().basePath
    fetch(`${basePath}/api/Horse/breeds`)
      .then((r) => r.json())
      .then(setBreeds)
      .catch(() => {})
    fetch(`${basePath}/api/Horse/colors`)
      .then((r) => r.json())
      .then(setColors)
      .catch(() => {})
  }, [])

  /* SEARCH HELPERS */
  const searchHorses = useCallback(async (q: string, gender: string): Promise<HorseSearchResultDto[]> => {
    try {
      const result = await new HorseApi(authState.apicfg()).apiHorseSearchGet({ q, gender })
      return result.items ?? []
    } catch {
      return []
    }
  }, [])

  const searchPersons = useCallback(async (q: string): Promise<PersonSearchResultDto[]> => {
    try {
      const result = await new PersonApi(authState.apicfg()).apiPersonSearchGet({ q })
      return result.items ?? []
    } catch {
      return []
    }
  }, [])

  /* DERIVED STATE */
  const formValid =
    !!form.name && form.name.trim().length > 0 &&
    !!form.breedId &&
    !!form.colorId &&
    !!form.gender &&
    (ownedByBooker || (!!form.owner && form.owner.trim().length > 0)) &&
    !!form.mother && form.mother.trim().length > 0 &&
    (!!form.motherBreedId || !!form.motherRegisterId) &&
    !!form.father && form.father.trim().length > 0 &&
    (!!form.fatherBreedId || !!form.fatherRegisterId) &&
    !!form.size &&
    !!form.birthday && form.birthday > NULL_DATE &&
    !!form.lNr && form.lNr.trim().length > 0

  /* LOGIC */
  const register = useCallback(() => {
    setLoading(true)
    if (bookerId == null) {
      setLoading(false)
    }

    new HorseRegisterApi(authState.apicfg())
      .apiHorseRegisterPostRaw({
        horseRegisterDto: { ...form, bookerId, bookerEmail }
      })
      .then(() => {
        appState.addToast('Registrierung erfolgreich!', 'success')
        setForm(EMPTY_HORSE_REGISTER)
        setMotherSearch('')
        setFatherSearch('')
        setBdDay('')
        setBdMonth('')
        setBdYear('')
        window.history.back()
      })
      .catch((err) => {
        appState.addToast('Registrierung fehlgeschlagen! Pr�fen Sie bitte die Felder', 'error')
        console.error(err)
      })
      .finally(() => setLoading(false))
  }, [form])

  /* TEMPLATE */
  const user = authState.user$.value
  const ibanMissing = !user?.bankAccountIBAN?.trim() || !user?.bankOwner?.trim()

  return (
    <>
      <Loading loading={loading} />
      {ibanMissing ? (
        <div className='card' style={{ maxWidth: '600px', margin: '1rem auto', textAlign: 'center', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontSize: '2rem' }}>⚠️</span>
          <p>Sie müssen für die Registrierung Ihres Pferdes erst eine gültige IBAN in Ihrer Adresse eintragen.</p>
          <Button label='Zur Adressänderung' onClick={() => (window.location.hash = '#/address-change')} />
        </div>
      ) : (
        <div className='card' style={{ maxWidth: '600px', margin: '1rem auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Neues Pferd registrieren</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            register()
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          {/* Name, Geschlecht & Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <InputText
              style={{ flex: 1 }}
              placeholder='Name *'
              value={form.name ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Geschlecht'
              value={form.gender ?? null}
              options={[
                { label: 'Stute', value: 'S' },
                { label: 'Hengst', value: 'H' },
                { label: 'Wallach', value: 'W' }
              ]}
              optionLabel='label'
              optionValue='value'
              onChange={(e) => setForm((v) => ({ ...v, gender: e.value ?? '' }))}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse'
              value={form.breedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, breedId: e.value ?? undefined }))}
            />
          </div>
          {/* Farbe & Besitzer */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Farbe'
              value={form.colorId ?? null}
              options={colors}
              optionLabel='name'
              optionValue='id'
              filter
              showClear
              onChange={(e) => setForm((v) => ({ ...v, colorId: e.value ?? undefined }))}
            />
            <InputText
              style={{ flex: 1 }}
              placeholder='Besitzer'
              value={form.owner ?? ''}
              disabled={ownedByBooker}
              onChange={(e) => setForm((v) => ({ ...v, owner: e.target.value ?? '' }))}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
              <Checkbox
                inputId='ownedByBooker'
                checked={ownedByBooker}
                onChange={(e) => {
                  const checked = e.checked ?? false
                  setOwnedByBooker(checked)
                  setForm((v) => ({
                    ...v,
                    ownerRegisterId: checked ? (bookerId ?? undefined) : undefined,
                    owner: checked ? '' : v.owner
                  }))
                }}
              />
              <label htmlFor='ownedByBooker'>Mein Eigenes</label>
            </div>
          </div>
          {/* Züchter */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <AutoComplete
              style={{ flex: 1 }}
              inputStyle={{ width: '100%' }}
              placeholder='Suchhilfe Züchter'
              value={breederSearch}
              suggestions={breederSuggestions}
              field='lastName'
              completeMethod={(e) => searchPersons(e.query).then(setBreederSuggestions)}
              onChange={(e) => setBreederSearch(typeof e.value === 'string' ? e.value : [((e.value as PersonSearchResultDto)?.firstName ?? ''), ((e.value as PersonSearchResultDto)?.lastName ?? '')].filter(Boolean).join(' '))}
              onSelect={(e) => {
                const item = e.value as PersonSearchResultDto
                const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ')
                setForm((v) => ({ ...v, breeder: fullName, breederRegisterId: item.registerId ?? undefined }))
                setBreederSearch('')
              }}
              itemTemplate={(item: PersonSearchResultDto) => (
                <span>{[item.firstName, item.lastName].filter(Boolean).join(' ')}</span>
              )}
            />
            <InputText
              style={{ flex: 1 }}
              placeholder='Züchter'
              value={form.breeder ?? ''}
              onChange={(e) => {
                const val = e.target.value ?? ''
                setForm((v) => ({
                  ...v,
                  breeder: val,
                  ...(val !== v.breeder ? { breederRegisterId: undefined } : {})
                }))
              }}
            />
          </div>
          {/* Mutter & Mutter-Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <AutoComplete
              style={{ flex: 1 }}
              inputStyle={{ width: '100%' }}
              placeholder='Suchhilfe Mutter'
              value={motherSearch}
              suggestions={motherSuggestions}
              field='name'
              completeMethod={(e) => searchHorses(e.query, 'female').then(setMotherSuggestions)}
              onChange={(e) => setMotherSearch(typeof e.value === 'string' ? e.value : (e.value as HorseSearchResultDto)?.name ?? '')}
              onSelect={(e) => {
                const item = e.value as HorseSearchResultDto
                const breedVal = item.breed?.trim().toLowerCase()
                const breedId = breeds.find((b) =>
                  b.name?.trim().toLowerCase() === breedVal ||
                  b.shortcut?.trim().toLowerCase() === breedVal
                )?.id
                setForm((v) => ({
                  ...v,
                  mother: item.name ?? '',
                  motherRegisterId: item.registerId ?? undefined,
                  motherBreedId: breedId ?? v.motherBreedId
                }))
                setMotherSearch('')
              }}
              itemTemplate={(item: HorseSearchResultDto) => (
                <span>{item.name}{item.breed ? ` (${item.breed})` : ''}</span>
              )}
            />
            <InputText
              style={{ flex: 1 }}
              placeholder='Mutter'
              value={form.mother ?? ''}
              onChange={(e) => {
                const val = e.target.value ?? ''
                setForm((v) => ({
                  ...v,
                  mother: val,
                  ...(val !== v.mother ? { motherRegisterId: undefined, motherBreedId: undefined } : {})
                }))
              }}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse Mutter'
              value={form.motherBreedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear={!form.motherRegisterId}
              disabled={!!form.motherRegisterId}
              onChange={(e) => setForm((v) => ({ ...v, motherBreedId: e.value ?? undefined }))}
            />
          </div>
          {/* Vater & Vater-Rasse */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <AutoComplete
              style={{ flex: 1 }}
              inputStyle={{ width: '100%' }}
              placeholder='Suchhilfe Vater'
              value={fatherSearch}
              suggestions={fatherSuggestions}
              field='name'
              completeMethod={(e) => searchHorses(e.query, 'male').then(setFatherSuggestions)}
              onChange={(e) => setFatherSearch(typeof e.value === 'string' ? e.value : (e.value as HorseSearchResultDto)?.name ?? '')}
              onSelect={(e) => {
                const item = e.value as HorseSearchResultDto
                const breedVal = item.breed?.trim().toLowerCase()
                const breedId = breeds.find((b) =>
                  b.name?.trim().toLowerCase() === breedVal ||
                  b.shortcut?.trim().toLowerCase() === breedVal
                )?.id
                setForm((v) => ({
                  ...v,
                  father: item.name ?? '',
                  fatherRegisterId: item.registerId ?? undefined,
                  fatherBreedId: breedId ?? v.fatherBreedId
                }))
                setFatherSearch('')
              }}
              itemTemplate={(item: HorseSearchResultDto) => (
                <span>{item.name}{item.breed ? ` (${item.breed})` : ''}</span>
              )}
            />
            <InputText
              style={{ flex: 1 }}
              placeholder='Vater'
              value={form.father ?? ''}
              onChange={(e) => {
                const val = e.target.value ?? ''
                setForm((v) => ({
                  ...v,
                  father: val,
                  ...(val !== v.father ? { fatherRegisterId: undefined, fatherBreedId: undefined } : {})
                }))
              }}
            />
            <Dropdown
              style={{ flex: 1 }}
              placeholder='Rasse Vater'
              value={form.fatherBreedId ?? null}
              options={breeds}
              optionLabel='name'
              optionValue='id'
              filter
              showClear={!form.fatherRegisterId}
              disabled={!!form.fatherRegisterId}
              onChange={(e) => setForm((v) => ({ ...v, fatherBreedId: e.value ?? undefined }))}
            />
          </div>
          {/* Stockmaß & Großpferd */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
            <InputNumber
              style={{ flex: 1 }}
              placeholder='Stockmaß (cm)'
              value={form.size ?? null}
              onValueChange={(e) => {
                const val = e.value ?? undefined
                let isBigHorse = form.isBigHorse
                if (val == null || val < 144) isBigHorse = false
                else if (val > 148) isBigHorse = true
                setForm((v) => ({ ...v, size: val, isBigHorse }))
              }}
              min={0}
              max={300}
              maxFractionDigits={0}
              suffix=' cm'
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
              <Checkbox
                inputId='isBigHorse'
                checked={!!form.isBigHorse}
                disabled={!form.size || form.size < 144 || form.size > 148}
                onChange={(e) => setForm((v) => ({ ...v, isBigHorse: e.checked ?? false }))}
              />
              <label htmlFor='isBigHorse'>Großpferd</label>
            </div>
          </div>
          {/* Geburtstag */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <InputText
              style={{ width: '3.5rem', textAlign: 'center' }}
              placeholder='TT'
              maxLength={2}
              value={bdDay}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                setBdDay(val)
                if (val.length === 2) refBdMonth.current?.focus()
                setForm((v) => ({ ...v, birthday: parseDate(val, bdMonth, bdYear) }))
              }}
            />
            <span style={{ color: '#888' }}>.</span>
            <InputText
              style={{ width: '3.5rem', textAlign: 'center' }}
              placeholder='MM'
              maxLength={2}
              value={bdMonth}
              ref={refBdMonth}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                setBdMonth(val)
                if (val.length === 2) refBdYear.current?.focus()
                setForm((v) => ({ ...v, birthday: parseDate(bdDay, val, bdYear) }))
              }}
            />
            <span style={{ color: '#888' }}>.</span>
            <InputText
              style={{ width: '5rem', textAlign: 'center' }}
              placeholder='JJJJ'
              maxLength={4}
              value={bdYear}
              ref={refBdYear}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                setBdYear(val)
                setForm((v) => ({ ...v, birthday: parseDate(bdDay, bdMonth, val) }))
              }}
            />
            <span style={{ color: '#aaa', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Geburtstag</span>
          </div>
          {/* Lebensnummer */}
          <InputText
            style={{ width: '100%' }}
            placeholder='Lebensnummer'
            value={form.lNr ?? ''}
            onChange={(e) => setForm((v) => ({ ...v, lNr: e.target.value ?? '' }))}
          />
          {/* Anmerkung */}
          <InputText
            style={{ width: '100%' }}
            placeholder='Anmerkung'
            value={form.remark ?? ''}
            onChange={(e) => setForm((v) => ({ ...v, remark: e.target.value ?? '' }))}
          />
        </form>
        <Button onClick={register} disabled={!formValid}>
          Registrieren
        </Button>
        <Button outlined onClick={() => window.history.back()}>
          Zurück
        </Button>
      </div>
      )}
    </>
  )
}
